// Copyright (c) 2026 Aravind Adari. All rights reserved.

import { useEffect, useState, useMemo, useCallback, memo } from "react";
import {
  getDocs,
  updateDoc,
  deleteDoc,
  setDoc,
  addDoc,
  query,
  where,
  Timestamp,
} from "firebase/firestore";
import { useClient } from "../contexts/ClientContext";
import { timed, perfMark, perfTotal } from "../utils/perf";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import type { PickersCalendarHeaderProps } from "@mui/x-date-pickers/PickersCalendarHeader";
import dayjs, { Dayjs } from "dayjs";
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Snackbar,
  Alert,
  Card,
  CardContent,
  Typography,
  Tabs,
  Tab,
  Chip,
  IconButton,
  Tooltip,
  Paper,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  SelectChangeEvent,
  Divider,
  LinearProgress,
  Avatar,
  Badge,
  TablePagination,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import PeopleIcon from "@mui/icons-material/People";
import BusinessIcon from "@mui/icons-material/Business";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import DashboardIcon from "@mui/icons-material/Dashboard";
import FactCheckIcon from "@mui/icons-material/FactCheck";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import VerifiedIcon from "@mui/icons-material/Verified";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import DownloadIcon from "@mui/icons-material/Download";
import RefreshIcon from "@mui/icons-material/Refresh";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import usePagination from "../hooks/usePagination";

/* ─── Types ─── */
interface EmployeeForm {
  id?: string;
  empId?: string;
  firstName: string;
  lastName: string;
  fullName: string;
  designation: string;
  baseSalary: number;
  status: string;
  branchId: string;
  linkedAuthUid: string;
  settlementAccountId: string;
  allowances: Record<string, number>;
  tdAllowancePct: number;
  dateOfJoining: string;
  bloodGroup: string;
  dob: string;
  fatherName: string;
  motherName: string;
  notes: string;
  educationalQualifications: string;
  skills: string;
}
interface LocationForm {
  id?: string;
  name: string;
  location: string;
}
interface AttendanceRow {
  id: string;
  empId: string;
  branchId: string;
  date: string;
  clockIn: Timestamp | null;
  clockOut: Timestamp | null;
  verified: boolean;
  createdBy: string;
}
interface LeaveRow {
  id: string;
  empId: string;
  startDate: string;
  endDate: string;
  type: string;
  status: string;
  reason: string;
  requestedBy: string;
  createdAt: Timestamp;
}
interface AllowanceType {
  id: string;
  name: string;
  description: string;
  amount: number;
}
interface PublicHoliday {
  id: string;
  date: string;
  name: string;
  year: number;
}


const emptyEmpForm: EmployeeForm = {
  firstName: "",
  lastName: "",
  fullName: "",
  designation: "",
  baseSalary: 0,
  status: "active",
  branchId: "",
  linkedAuthUid: "",
  settlementAccountId: "",
  allowances: {},
  tdAllowancePct: 10,
  dateOfJoining: "",
  bloodGroup: "",
  dob: "",
  fatherName: "",
  motherName: "",
  notes: "",
  educationalQualifications: "",
  skills: "",
};

const BLOOD_GROUPS = ["A+", "A-", "B+", "B-", "O+", "O-", "AB+", "AB-"];
const emptyLocForm: LocationForm = { name: "", location: "" };

/* ─── Stat Card ─── */
function StatCard({ icon, value, label, color, bgColor, onClick }: {
  icon: React.ReactNode;
  value: string | number;
  label: string;
  color: string;
  bgColor: string;
  onClick?: () => void;
}) {
  return (
    <Card sx={{ flex: "1 1 180px", minWidth: 180, transition: "transform 0.2s", "&:hover": { transform: "translateY(-2px)" }, ...(onClick ? { cursor: "pointer" } : {}) }} onClick={onClick}>
      <CardContent sx={{ display: "flex", alignItems: "center", gap: 2, py: 2.5 }}>
        <Avatar sx={{ width: 52, height: 52, bgcolor: (t) => t.palette.mode === "light" ? bgColor : `${color}33`, color }}>{icon}</Avatar>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 800, lineHeight: 1.1 }}>{value}</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>{label}</Typography>
        </Box>
      </CardContent>
    </Card>
  );
}

type DateView = "day" | "month" | "year";

const SmartCalendarHeader = ({
  currentMonth,
  onMonthChange,
  onViewChange,
  view,
}: PickersCalendarHeaderProps) => (
  <Box sx={{ display: "flex", alignItems: "center", px: 1, py: 0.5, minHeight: 44 }}>
    {view === "day" ? (
      <>
        <IconButton size="small" onClick={() => onMonthChange(currentMonth.subtract(1, "month"))}>
          <ChevronLeftIcon fontSize="small" />
        </IconButton>
        <Box sx={{ flex: 1, display: "flex", justifyContent: "center", gap: 0 }}>
          <Button size="small" onClick={() => onViewChange?.("month")} sx={{ textTransform: "none", fontWeight: 700, fontSize: "0.875rem", minWidth: 0, px: 0.75 }}>
            {currentMonth.format("MMMM")}
          </Button>
          <Button size="small" onClick={() => onViewChange?.("year")} sx={{ textTransform: "none", fontWeight: 700, fontSize: "0.875rem", minWidth: 0, px: 0.75 }}>
            {currentMonth.format("YYYY")}
          </Button>
        </Box>
        <IconButton size="small" onClick={() => onMonthChange(currentMonth.add(1, "month"))}>
          <ChevronRightIcon fontSize="small" />
        </IconButton>
      </>
    ) : (
      <>
        <IconButton size="small" onClick={() => onViewChange?.("day")} sx={{ mr: 1 }}>
          <ArrowBackIcon fontSize="small" />
        </IconButton>
        <Typography variant="subtitle2" sx={{ flex: 1, fontWeight: 700, color: "text.secondary" }}>
          {view === "year" ? "Select Year" : "Select Month"}
        </Typography>
      </>
    )}
  </Box>
);

// Isolated so parent re-renders (data loading, state updates) don't reset the calendar view
const AttendanceDatePicker = memo(({ value, onChange }: { value: string; onChange: (d: string) => void }) => {
  const valueDayjs = useMemo(() => dayjs(value), [value]);
  const maxDate = useMemo(() => dayjs(), []);
  const [calView, setCalView] = useState<DateView>("day");
  return (
    <DatePicker
      label="Date"
      value={valueDayjs}
      onChange={(v: Dayjs | null) => { if (v) { onChange(v.format("YYYY-MM-DD")); setCalView("day"); } }}
      onViewChange={(v) => setCalView(v as DateView)}
      view={calView}
      views={["year", "month", "day"]}
      format="DD/MMM/YYYY"
      maxDate={maxDate}
      slots={{ calendarHeader: SmartCalendarHeader }}
      slotProps={{ textField: { size: "small", sx: { width: 180 } } }}
    />
  );
});

export default function AdminPage() {
  const { col, docRef, timesheetEditDays } = useClient();
  const [tab, setTab] = useState(0);
  const [employees, setEmployees] = useState<(EmployeeForm & { id: string })[]>([]);
  const [locations, setLocations] = useState<(LocationForm & { id: string })[]>([]);
  const [attendance, setAttendance] = useState<AttendanceRow[]>([]);
  const [leaveRequests, setLeaveRequests] = useState<LeaveRow[]>([]);
  const [allowanceTypes, setAllowanceTypes] = useState<AllowanceType[]>([]);
  const [publicHolidays, setPublicHolidays] = useState<PublicHoliday[]>([]);
  const [empOpen, setEmpOpen] = useState(false);
  const [locOpen, setLocOpen] = useState(false);
  const [empForm, setEmpForm] = useState<EmployeeForm>({ ...emptyEmpForm });
  const [locForm, setLocForm] = useState<LocationForm>({ ...emptyLocForm });
  const [msg, setMsg] = useState("");
  const [msgType, setMsgType] = useState<"success" | "error" | "info">("success");
  const [attDateFilter, setAttDateFilter] = useState(dayjs().format("YYYY-MM-DD"));
  const [attLocFilter, setAttLocFilter] = useState("");
  const handleAttDateChange = useCallback((d: string) => setAttDateFilter(d), []);
  const [loading, setLoading] = useState(false);
  // Public holidays
  const [phYear, setPhYear] = useState(dayjs().year());
  const [phOpen, setPhOpen] = useState(false);
  const [phDate, setPhDate] = useState("");
  const [phName, setPhName] = useState("");

  // Pagination
  const empPag = usePagination(employees);
  const attPag = usePagination(attendance);
  const leavePag = usePagination(leaveRequests);

  const showMsg = (text: string, type: "success" | "error" | "info" = "success") => {
    setMsg(text);
    setMsgType(type);
  };

  /* ─── Data loading ─── */
  const loadEmployees = async () => {
    try {
      const snap = await timed("AdminPage.loadEmployees", () => getDocs(col("employees")));
      setEmployees(snap.docs.map((d) => {
        const data = d.data() as Record<string, unknown>;
        return {
          id: d.id,
          empId: (data.empId as string) ?? d.id,
          firstName: (data.firstName as string) ?? "",
          lastName: (data.lastName as string) ?? "",
          fullName: (data.fullName as string) ?? "",
          designation: (data.designation as string) ?? (data.role as string) ?? "",
          baseSalary: Number(data.baseSalary ?? 0),
          status: (data.status as string) ?? "active",
          branchId: (data.branchId as string) ?? "",
          linkedAuthUid: (data.linkedAuthUid as string) ?? "",
          settlementAccountId: (data.settlementAccountId as string) ?? "",
          allowances: (data.allowances as Record<string, number>) ?? {},
          tdAllowancePct: Number(data.tdAllowancePct ?? 10),
          dateOfJoining: (data.dateOfJoining as string) ?? "",
          bloodGroup: (data.bloodGroup as string) ?? "",
          dob: (data.dob as string) ?? "",
          fatherName: (data.fatherName as string) ?? "",
          motherName: (data.motherName as string) ?? "",
          notes: (data.notes as string) ?? "",
          educationalQualifications: (data.educationalQualifications as string) ?? "",
          skills: (data.skills as string) ?? "",
        };
      }));
    } catch {
      showMsg("Failed to load employees.", "error");
    }
  };
  const loadLocations = async () => {
    try {
      const snap = await timed("AdminPage.loadLocations", () => getDocs(col("branches")));
      setLocations(snap.docs.map((d) => ({ id: d.id, ...(d.data() as LocationForm) })).sort((a, b) => {
        if (a.name.toLowerCase().startsWith("anakapalli")) return -1;
        if (b.name.toLowerCase().startsWith("anakapalli")) return 1;
        return a.name.localeCompare(b.name);
      }));
    } catch {
      showMsg("Failed to load locations.", "error");
    }
  };
  const loadAttendance = async (date?: string) => {
    try {
      const d = date ?? attDateFilter;
      const constraints: ReturnType<typeof where>[] = [where("date", "==", d)];
      if (attLocFilter) constraints.push(where("branchId", "==", attLocFilter));
      const q = query(col("attendance"), ...constraints);
      const snap = await timed(`AdminPage.loadAttendance(${d})`, () => getDocs(q));
      setAttendance(snap.docs.map((dc) => ({ id: dc.id, ...(dc.data() as Omit<AttendanceRow, "id">) })));
    } catch {
      showMsg("Failed to load attendance.", "error");
    }
  };
  const loadLeaves = async () => {
    try {
      const snap = await timed("AdminPage.loadLeaves", () => getDocs(col("leaveRequests")));
      setLeaveRequests(snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<LeaveRow, "id">) })));
    } catch {
      showMsg("Failed to load leave requests.", "error");
    }
  };
  const loadAllowanceTypes = async () => {
    try {
      const snap = await timed("AdminPage.loadAllowanceTypes", () => getDocs(col("allowanceTypes")));
      setAllowanceTypes(snap.docs.map((d) => {
        const data = d.data() as Record<string, unknown>;
        return { id: d.id, name: (data.name as string) ?? "", description: (data.description as string) ?? "", amount: Number(data.amount ?? 0) };
      }));
    } catch {
      showMsg("Failed to load allowance types.", "error");
    }
  };
  const loadPublicHolidays = async () => {
    try {
      const snap = await timed("AdminPage.loadPublicHolidays", () => getDocs(col("publicHolidays")));
      setPublicHolidays(snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<PublicHoliday, "id">) })));
    } catch {
      showMsg("Failed to load public holidays.", "error");
    }
  };
  useEffect(() => {
    setLoading(true);
    const t0 = performance.now();
    Promise.all([loadEmployees(), loadLocations(), loadAttendance(), loadLeaves(), loadAllowanceTypes(), loadPublicHolidays()]).finally(() => {
      perfTotal("AdminPage.load TOTAL", t0);
      setLoading(false);
    });
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (tab === 3) loadAttendance();
    // eslint-disable-next-line
  }, [attDateFilter, attLocFilter]);

  /* ─── Helpers ─── */
  const getEmpName = (empId: string) => {
    const e = employees.find((emp) => emp.id === empId || emp.empId === empId);
    if (!e) return empId;
    if (e.firstName && e.lastName) return `${e.firstName} ${e.lastName}`;
    return e.fullName || empId;
  };
  const getLocName = (branchId: string) => locations.find((l) => l.id === branchId)?.name ?? (branchId || "—");
  const formatTs = (ts: Timestamp | null) => (ts ? dayjs(ts.toDate()).format("hh:mm A") : "—");

  /* ─── Employee CRUD ─── */
  const openEmpDialog = (emp?: EmployeeForm & { id: string }) => {
    if (emp) {
      // Pre-fill missing allowance values with defaults from allowance types
      const mergedAllowances = { ...emp.allowances };
      allowanceTypes.forEach((at) => {
        if (mergedAllowances[at.id] === undefined || mergedAllowances[at.id] === null) {
          mergedAllowances[at.id] = at.amount;
        }
      });
      setEmpForm({ ...emp, allowances: mergedAllowances });
    } else {
      const defaultAllowances: Record<string, number> = {};
      allowanceTypes.forEach((at) => { defaultAllowances[at.id] = at.amount; });
      setEmpForm({ ...emptyEmpForm, allowances: defaultAllowances });
    }
    setEmpOpen(true);
  };
  const saveEmployee = async () => {
    if (!empForm.firstName.trim()) return showMsg("First name is required.", "error");
    if (!empForm.lastName.trim()) return showMsg("Last name is required.", "error");
    if (!empForm.designation.trim()) return showMsg("Designation is required.", "error");
    if (!empForm.branchId) return showMsg("Please select a location.", "error");
    if (empForm.baseSalary <= 0) return showMsg("Base salary must be greater than zero.", "error");
    const done = perfMark(`AdminPage.saveEmployee(${empForm.firstName} ${empForm.lastName})`);
    try {
      const fullName = `${empForm.firstName.trim()} ${empForm.lastName.trim()}`;
      const { id, empId: _eid, ...data } = empForm;
      const saveData = { ...data, fullName };
      if (id) {
        await updateDoc(docRef("employees", id), saveData);
      } else {
        const newEmpId = `E${Date.now()}`;
        await setDoc(docRef("employees", newEmpId), { ...saveData, empId: newEmpId });
      }
      done();
      setEmpOpen(false);
      showMsg(id ? "Employee updated." : "Employee added.");
      loadEmployees();
    } catch {
      showMsg("Failed to save employee.", "error");
    }
  };
  const removeEmployee = async (id: string) => {
    if (window.confirm("Delete this employee? This cannot be undone.")) {
      try {
        await deleteDoc(docRef("employees", id));
        showMsg("Employee deleted.");
        loadEmployees();
      } catch {
        showMsg("Failed to delete employee.", "error");
      }
    }
  };

  /* ─── Location CRUD ─── */
  const openLocDialog = (loc?: LocationForm & { id: string }) => {
    setLocForm(loc ? { ...loc } : { ...emptyLocForm });
    setLocOpen(true);
  };
  const saveLocation = async () => {
    if (!locForm.name.trim()) return showMsg("Location name is required.", "error");
    if (!locForm.location.trim()) return showMsg("Location address is required.", "error");
    try {
      if (locForm.id) {
        const { id, ...data } = locForm;
        await updateDoc(docRef("branches", id), data);
      } else {
        await addDoc(col("branches"), { name: locForm.name.trim(), location: locForm.location.trim() });
      }
      setLocOpen(false);
      showMsg(locForm.id ? "Location updated." : "Location added.");
      loadLocations();
    } catch {
      showMsg("Failed to save location.", "error");
    }
  };
  const removeLocation = async (id: string) => {
    const locEmps = employees.filter((e) => e.branchId === id);
    if (locEmps.length > 0) return showMsg(`Cannot delete — ${locEmps.length} employee(s) assigned.`, "error");
    if (window.confirm("Delete this location?")) {
      try {
        await deleteDoc(docRef("branches", id));
        showMsg("Location deleted.");
        loadLocations();
      } catch {
        showMsg("Failed to delete location.", "error");
      }
    }
  };

  /* ─── Attendance Validation ─── */
  const verifyAttendance = async (attId: string) => {
    const record = attendance.find((a) => a.id === attId);
    if (record && !record.clockIn && !record.clockOut) {
      return showMsg("Cannot verify: no clock-in or clock-out data.", "error");
    }
    try {
      await updateDoc(docRef("attendance", attId), { verified: true });
      showMsg("Timesheet verified.");
      loadAttendance();
    } catch {
      showMsg("Failed to verify timesheet.", "error");
    }
  };
  const deleteAttendance = async (attId: string) => {
    if (window.confirm("Delete this timesheet record?")) {
      // Optimistic update — remove from UI immediately
      setAttendance((prev) => prev.filter((a) => a.id !== attId));
      try {
        await deleteDoc(docRef("attendance", attId));
        showMsg("Attendance deleted.");
      } catch {
        showMsg("Failed to delete attendance.", "error");
        loadAttendance();
      }
    }
  };

  /* ─── Leave Approval ─── */
  const updateLeaveStatus = async (leaveId: string, status: string) => {
    try {
      await updateDoc(docRef("leaveRequests", leaveId), { status });
      showMsg(`Leave ${status}.`);
      loadLeaves();
    } catch {
      showMsg("Failed to update leave.", "error");
    }
  };



  /* ─── Public Holidays ─── */
  const savePublicHoliday = async () => {
    if (!phDate) return showMsg("Date is required.", "error");
    if (!phName.trim()) return showMsg("Holiday name is required.", "error");
    try {
      await addDoc(col("publicHolidays"), {
        date: phDate,
        name: phName.trim(),
        year: dayjs(phDate).year(),
      });
      setPhOpen(false);
      setPhDate("");
      setPhName("");
      showMsg("Public holiday added.");
      loadPublicHolidays();
    } catch {
      showMsg("Failed to save holiday.", "error");
    }
  };
  const deletePublicHoliday = async (id: string) => {
    if (window.confirm("Delete this holiday?")) {
      try {
        await deleteDoc(docRef("publicHolidays", id));
        showMsg("Holiday deleted.");
        loadPublicHolidays();
      } catch {
        showMsg("Failed to delete.", "error");
      }
    }
  };

  /* ─── Report data ─── */
  const reportData = useMemo(() => {
    const activeEmps = employees.filter((e) => e.status === "active");
    return {
      totalEmployees: employees.length,
      activeEmployees: activeEmps.length,
      totalLocations: locations.length,
      pendingLeaves: leaveRequests.filter((l) => l.status === "pending").length,
      approvedLeaves: leaveRequests.filter((l) => l.status === "approved").length,
      todayAttendance: attendance.length,
      verifiedToday: attendance.filter((a) => a.verified).length,
      unverifiedToday: attendance.filter((a) => !a.verified).length,
      avgSalary: employees.length > 0 ? Math.round(activeEmps.reduce((s, e) => s + Number(e.baseSalary || 0), 0) / (activeEmps.length || 1)) : 0,
      totalPayroll: activeEmps.reduce((s, e) => s + Number(e.baseSalary || 0), 0),
    };
  }, [employees, locations, attendance, leaveRequests]);

  /* ─── CSV Exports ─── */
  const exportAttendanceCsv = () => {
    const header = "Employee,Location,Date,Clock In,Clock Out,Verified\n";
    const rows = attendance.map((a) =>
      `"${getEmpName(a.empId)}","${getLocName(a.branchId)}","${a.date}","${formatTs(a.clockIn)}","${formatTs(a.clockOut)}","${a.verified ? "Yes" : "No"}"`
    ).join("\n");
    const blob = new Blob([header + rows], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `attendance-${attDateFilter}.csv`;
    link.click();
    URL.revokeObjectURL(url);
    showMsg("CSV downloaded.", "info");
  };
  const exportEmployeesCsv = () => {
    const header = "ID,First Name,Last Name,Designation,Location,Base Salary,Status\n";
    const rows = employees.map((e) =>
      `"${e.empId ?? e.id}","${e.firstName}","${e.lastName}","${e.designation}","${getLocName(e.branchId)}","${e.baseSalary}","${e.status}"`
    ).join("\n");
    const blob = new Blob([header + rows], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `employees-${dayjs().format("YYYY-MM-DD")}.csv`;
    link.click();
    URL.revokeObjectURL(url);
    showMsg("Employees CSV downloaded.", "info");
  };

  const filteredHolidays = publicHolidays.filter((h) => h.year === phYear).sort((a, b) => a.date.localeCompare(b.date));
  const holPag = usePagination(filteredHolidays);

  return (
    <Box>
      {loading && <LinearProgress sx={{ mb: 2, borderRadius: 1 }} />}

      {/* ═══ Header ═══ */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 800, color: "primary.main" }}>
            Dashboard
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage employees, locations, attendance, and leaves
          </Typography>
        </Box>
        <Tooltip title="Refresh all data">
          <IconButton
            onClick={() => { setLoading(true); Promise.all([loadEmployees(), loadLocations(), loadAttendance(), loadLeaves(), loadAllowanceTypes(), loadPublicHolidays()]).finally(() => setLoading(false)); }}
            sx={{ bgcolor: (t) => t.palette.mode === "light" ? "#e8f5e9" : "rgba(46,125,50,0.15)", "&:hover": { bgcolor: (t) => t.palette.mode === "light" ? "#c8e6c9" : "rgba(46,125,50,0.25)" } }}
          >
            <RefreshIcon color="primary" />
          </IconButton>
        </Tooltip>
      </Box>

      {/* ═══ Tabs ═══ */}
      <Paper sx={{ overflow: "hidden", mb: 3 }}>
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ borderBottom: 1, borderColor: "divider", px: 1, "& .MuiTab-root": { minHeight: 56, textTransform: "none", fontWeight: 600 } }}
        >
          <Tab icon={<DashboardIcon />} iconPosition="start" label="Overview" />
          <Tab icon={<PeopleIcon />} iconPosition="start" label="Employees" />
          <Tab icon={<BusinessIcon />} iconPosition="start" label="Locations" />
          <Tab icon={<FactCheckIcon />} iconPosition="start" label={
            <Badge badgeContent={reportData.unverifiedToday} color="warning" max={99}><span style={{ paddingRight: 8 }}>Timesheet</span></Badge>
          } />
          <Tab icon={<EventAvailableIcon />} iconPosition="start" label={
            <Badge badgeContent={reportData.pendingLeaves} color="error" max={99}><span style={{ paddingRight: 8 }}>Leaves</span></Badge>
          } />
          <Tab icon={<CalendarMonthIcon />} iconPosition="start" label="Public Holidays" />
        </Tabs>

        {/* ══════ TAB 0 — Overview ══════ */}
        {tab === 0 && (
          <Box sx={{ p: 3 }}>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, mb: 3 }}>
              <StatCard icon={<PeopleIcon />} value={reportData.totalEmployees} label="Total Employees" color="#F9A825" bgColor="#F9A82522" onClick={() => setTab(1)} />
              <StatCard icon={<PersonAddIcon />} value={reportData.activeEmployees} label="Active" color="#1565C0" bgColor="#1565C022" />
              <StatCard icon={<BusinessIcon />} value={reportData.totalLocations} label="Locations" color="#C62828" bgColor="#C6282822" onClick={() => setTab(2)} />
              <StatCard icon={<AccessTimeIcon />} value={reportData.todayAttendance} label="Today's Punches" color="#2E7D32" bgColor="#2E7D3222" />
            </Box>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, mb: 3 }}>
              <StatCard icon={<VerifiedIcon />} value={reportData.verifiedToday} label="Verified Today" color="#1565C0" bgColor="#1565C022" />
              <StatCard icon={<FactCheckIcon />} value={reportData.unverifiedToday} label="Pending Verify" color="#C62828" bgColor="#C6282822" />
              <StatCard icon={<EventAvailableIcon />} value={reportData.pendingLeaves} label="Pending Leaves" color="#F9A825" bgColor="#F9A82522" onClick={() => setTab(4)} />
              <StatCard icon={<TrendingUpIcon />} value={`₹${(reportData.totalPayroll / 100000).toFixed(1)}L`} label="Monthly Payroll" color="#2E7D32" bgColor="#2E7D3222" />
            </Box>

            {/* Quick actions */}
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 700 }}>Quick Actions</Typography>
              <Divider sx={{ mb: 2 }} />
              <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                <Button variant="outlined" startIcon={<PersonAddIcon />} onClick={() => { setTab(1); setTimeout(() => openEmpDialog(), 100); }}>Add Employee</Button>
                <Button variant="outlined" startIcon={<BusinessIcon />} onClick={() => { setTab(2); setTimeout(() => openLocDialog(), 100); }}>Add Location</Button>
                <Button variant="outlined" startIcon={<FactCheckIcon />} onClick={() => setTab(3)}>Validate Attendance</Button>
                <Button variant="outlined" startIcon={<EventAvailableIcon />} onClick={() => setTab(4)}>Review Leaves ({reportData.pendingLeaves})</Button>
                <Button variant="outlined" startIcon={<DownloadIcon />} onClick={exportEmployeesCsv}>Export Employees</Button>
              </Box>
            </Paper>
          </Box>
        )}

        {/* ══════ TAB 1 — Employees ══════ */}
        {tab === 1 && (
          <Box sx={{ p: 3 }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>Employee Directory</Typography>
              <Box sx={{ display: "flex", gap: 1 }}>
                <Button variant="outlined" size="small" startIcon={<DownloadIcon />} onClick={exportEmployeesCsv}>Export</Button>
                <Button variant="contained" startIcon={<AddIcon />} onClick={() => openEmpDialog()}>Add Employee</Button>
              </Box>
            </Box>
            <TableContainer sx={{ borderRadius: 2, border: 1, borderColor: "divider" }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Designation</TableCell>
                    <TableCell>Location</TableCell>
                    <TableCell align="center">Status</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {employees.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={99} align="center" sx={{ py: 6, color: "text.secondary" }}>
                        <PeopleIcon sx={{ fontSize: 48, opacity: 0.3, mb: 1 }} /><br />
                        No employees yet. Click "Add Employee" to get started.
                      </TableCell>
                    </TableRow>
                  )}
                  {empPag.paginatedItems.map((e) => (
                    <TableRow key={e.id} sx={{ "&:hover": { bgcolor: "action.hover" } }}>
                      <TableCell>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                          <Avatar sx={{ width: 32, height: 32, bgcolor: "primary.main", fontSize: "0.85rem" }}>
                            {(e.firstName || e.fullName || "?").charAt(0)}
                          </Avatar>
                          <Typography sx={{ fontWeight: 600 }}>
                            {e.firstName && e.lastName ? `${e.firstName} ${e.lastName}` : e.fullName || "—"}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell sx={{ textTransform: "capitalize" }}>{e.designation || "—"}</TableCell>
                      <TableCell>{getLocName(e.branchId)}</TableCell>
                      <TableCell align="center">
                        <Chip label={e.status} size="small" color={e.status === "active" ? "success" : e.status === "terminated" ? "error" : "default"} sx={{ textTransform: "capitalize", fontWeight: 600 }} />
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title="Edit"><IconButton size="small" color="primary" onClick={() => openEmpDialog(e)}><EditIcon fontSize="small" /></IconButton></Tooltip>
                        <Tooltip title="Delete"><IconButton size="small" color="error" onClick={() => removeEmployee(e.id)}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            {employees.length > 0 && (
              <TablePagination component="div" count={empPag.totalCount} page={empPag.page} onPageChange={empPag.onPageChange} rowsPerPage={empPag.rowsPerPage} onRowsPerPageChange={empPag.onRowsPerPageChange} rowsPerPageOptions={[10, 25, 50]} />
            )}
          </Box>
        )}

        {/* ══════ TAB 2 — Locations ══════ */}
        {tab === 2 && (
          <Box sx={{ p: 3 }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>Locations</Typography>
              <Button variant="contained" startIcon={<AddIcon />} onClick={() => openLocDialog()}>Add Location</Button>
            </Box>
            <Box sx={{ display: "grid", gridTemplateColumns: `repeat(${locations.length >= 5 ? 3 : Math.min(locations.length, 2)}, 1fr)`, gap: 2 }}>
              {locations.length === 0 && (
                <Paper sx={{ p: 4, textAlign: "center", gridColumn: "1 / -1", color: "text.secondary" }}>
                  <BusinessIcon sx={{ fontSize: 48, opacity: 0.3, mb: 1 }} /><br />
                  No locations yet. Click "Add Location" to create one.
                </Paper>
              )}
              {[...locations].sort((a, b) => employees.filter((e) => e.branchId === b.id).length - employees.filter((e) => e.branchId === a.id).length).map((l) => {
                const locEmps = employees.filter((e) => e.branchId === l.id);
                const activeCount = locEmps.filter((e) => e.status === "active").length;
                const activeEmps = locEmps.filter((e) => e.status === "active");
                const todayPunches = attendance.filter((a) => a.branchId === l.id).length;
                const locPendingLeaves = leaveRequests.filter((lr) => lr.status === "pending" && activeEmps.some((e) => e.id === lr.empId || e.empId === lr.empId)).length;
                return (
                  <Card key={l.id} sx={{ transition: "transform 0.2s", "&:hover": { transform: "translateY(-2px)", boxShadow: 6 } }}>
                    <CardContent>
                      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                        <Box>
                          <Typography variant="h6" sx={{ fontWeight: 700 }}>{l.name}</Typography>
                          <Typography variant="body2" color="text.secondary">{l.location || "No address set"}</Typography>
                        </Box>
                        <Box>
                          <IconButton size="small" color="primary" onClick={() => openLocDialog(l)}><EditIcon fontSize="small" /></IconButton>
                          <IconButton size="small" color="error" onClick={() => removeLocation(l.id)}><DeleteIcon fontSize="small" /></IconButton>
                        </Box>
                      </Box>
                      <Divider sx={{ my: 1.5 }} />
                      <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                        <Box sx={{ textAlign: "center", flex: 1, minWidth: 60 }}>
                          <Typography variant="h6" sx={{ fontWeight: 700, color: "primary.main" }}>{locEmps.length}</Typography>
                          <Typography variant="caption" color="text.secondary">Total</Typography>
                        </Box>
                        <Divider orientation="vertical" flexItem />
                        <Box sx={{ textAlign: "center", flex: 1, minWidth: 60 }}>
                          <Typography variant="h6" sx={{ fontWeight: 700, color: "secondary.main" }}>{activeCount}</Typography>
                          <Typography variant="caption" color="text.secondary">Active</Typography>
                        </Box>
                        <Divider orientation="vertical" flexItem />
                        <Box sx={{ textAlign: "center", flex: 1, minWidth: 60 }}>
                          <Typography variant="h6" sx={{ fontWeight: 700, color: "warning.main" }}>{todayPunches}</Typography>
                          <Typography variant="caption" color="text.secondary">Punches Today</Typography>
                        </Box>
                        <Divider orientation="vertical" flexItem />
                        <Box sx={{ textAlign: "center", flex: 1, minWidth: 60 }}>
                          <Typography variant="h6" sx={{ fontWeight: 700, color: "error.main" }}>{locPendingLeaves}</Typography>
                          <Typography variant="caption" color="text.secondary">Pending Leaves</Typography>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                );
              })}
            </Box>
          </Box>
        )}

        {/* ══════ TAB 3 — Attendance ══════ */}
        {tab === 3 && (
          <Box sx={{ p: 3 }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2, flexWrap: "wrap", gap: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>Timesheet Validation</Typography>
              <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
                <AttendanceDatePicker value={attDateFilter} onChange={handleAttDateChange} />
                <FormControl size="small" sx={{ minWidth: 160 }}>
                  <InputLabel>Location</InputLabel>
                  <Select value={attLocFilter} label="Location" onChange={(e: SelectChangeEvent) => setAttLocFilter(e.target.value)}>
                    <MenuItem value="">All Locations</MenuItem>
                    {locations.map((l) => <MenuItem key={l.id} value={l.id}>{l.name}</MenuItem>)}
                  </Select>
                </FormControl>
                <Button variant="outlined" size="small" startIcon={<DownloadIcon />} onClick={exportAttendanceCsv}>Export</Button>
              </Box>
            </Box>
            {attendance.length === 0 ? (
              <Box sx={{ textAlign: "center", py: 8, color: "text.secondary" }}>
                <FactCheckIcon sx={{ fontSize: 56, opacity: 0.25, mb: 1 }} />
                <Typography variant="body2">No attendance records for {dayjs(attDateFilter).format("DD/MMM/YYYY")}.</Typography>
              </Box>
            ) : (
            <>
            <TableContainer sx={{ borderRadius: 2, border: 1, borderColor: "divider" }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Employee</TableCell>
                    <TableCell>Date</TableCell>
                    <TableCell align="center">Clock In</TableCell>
                    <TableCell align="center">Clock Out</TableCell>
                    <TableCell align="center">Hours</TableCell>
                    <TableCell align="center">Status</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {attPag.paginatedItems.map((a) => {
                    let ms = a.clockIn && a.clockOut ? a.clockOut.toDate().getTime() - a.clockIn.toDate().getTime() : 0;
                    const aData = a as unknown as Record<string, unknown>;
                    if (aData.lunchOut && aData.lunchIn) {
                      ms -= ((aData.lunchIn as Timestamp).toDate().getTime() - (aData.lunchOut as Timestamp).toDate().getTime());
                    }
                    const hours = a.clockIn && a.clockOut ? (ms / 3600000).toFixed(1) : "—";
                    const dayFrac = hours !== "—" ? " (1 day)" : "";
                    return (
                      <TableRow key={a.id}>
                        <TableCell>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <Avatar sx={{ width: 28, height: 28, fontSize: "0.75rem", bgcolor: "primary.main" }}>{getEmpName(a.empId).charAt(0)}</Avatar>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>{getEmpName(a.empId)}</Typography>
                          </Box>
                        </TableCell>
                        <TableCell>{dayjs(a.date).format("DD/MMM/YYYY")}</TableCell>
                        <TableCell align="center"><Chip label={formatTs(a.clockIn)} size="small" color="success" variant="outlined" sx={{ fontWeight: 600 }} /></TableCell>
                        <TableCell align="center">
                          {a.clockOut ? <Chip label={formatTs(a.clockOut)} size="small" color="error" variant="outlined" sx={{ fontWeight: 600 }} /> : <Chip label="Active" size="small" color="warning" sx={{ fontWeight: 600 }} />}
                        </TableCell>
                        <TableCell align="center" sx={{ fontWeight: 600, color: hours !== "—" && parseFloat(hours) < 8 ? "warning.main" : "inherit" }}>{hours}{hours !== "—" ? `h${dayFrac}` : ""}</TableCell>
                        <TableCell align="center">
                          {a.verified ? <Chip icon={<VerifiedIcon />} label="Verified" size="small" color="success" sx={{ fontWeight: 600 }} /> : <Chip label="Unverified" size="small" color="warning" variant="outlined" sx={{ fontWeight: 600 }} />}
                        </TableCell>
                        <TableCell align="center">
                          {!a.verified && (
                            <Tooltip title="Verify"><IconButton size="small" color="success" onClick={() => verifyAttendance(a.id)}><CheckCircleIcon fontSize="small" /></IconButton></Tooltip>
                          )}
                          <Tooltip title="Delete"><IconButton size="small" color="error" onClick={() => deleteAttendance(a.id)}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination component="div" count={attPag.totalCount} page={attPag.page} onPageChange={attPag.onPageChange} rowsPerPage={attPag.rowsPerPage} onRowsPerPageChange={attPag.onRowsPerPageChange} rowsPerPageOptions={[10, 25, 50]} />
            </>
            )}
          </Box>
        )}

        {/* ══════ TAB 4 — Leaves ══════ */}
        {tab === 4 && (
          <Box sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>Leave Requests</Typography>
            <TableContainer sx={{ borderRadius: 2, border: 1, borderColor: "divider" }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Employee</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>From</TableCell>
                    <TableCell>To</TableCell>
                    <TableCell align="center">Days</TableCell>
                    <TableCell>Reason</TableCell>
                    <TableCell align="center">Status</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {leaveRequests.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={99} align="center" sx={{ py: 6, color: "text.secondary" }}>
                        <EventAvailableIcon sx={{ fontSize: 48, opacity: 0.3, mb: 1 }} /><br />No leave requests.
                      </TableCell>
                    </TableRow>
                  )}
                  {leavePag.paginatedItems.sort((a, b) => (a.status === "pending" ? -1 : 1)).map((l) => {
                    const days = dayjs(l.endDate).diff(dayjs(l.startDate), "day") + 1;
                    return (
                      <TableRow key={l.id} sx={{ bgcolor: l.status === "pending" ? "warning.light" : "inherit" }}>
                        <TableCell>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <Avatar sx={{ width: 28, height: 28, fontSize: "0.75rem", bgcolor: "warning.main" }}>{getEmpName(l.empId).charAt(0)}</Avatar>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>{getEmpName(l.empId)}</Typography>
                          </Box>
                        </TableCell>
                        <TableCell sx={{ textTransform: "capitalize" }}>{l.type}</TableCell>
                        <TableCell>{dayjs(l.startDate).format("DD/MMM/YYYY")}</TableCell>
                        <TableCell>{dayjs(l.endDate).format("DD/MMM/YYYY")}</TableCell>
                        <TableCell align="center"><Chip label={`${days}d`} size="small" variant="outlined" /></TableCell>
                        <TableCell sx={{ maxWidth: 200 }}><Typography variant="body2" noWrap>{l.reason || "—"}</Typography></TableCell>
                        <TableCell align="center">
                          <Chip label={l.status} size="small" color={l.status === "approved" ? "success" : l.status === "rejected" ? "error" : "warning"} sx={{ textTransform: "capitalize", fontWeight: 600 }} />
                        </TableCell>
                        <TableCell align="center">
                          {l.status === "pending" && (
                            <>
                              <Tooltip title="Approve"><IconButton size="small" color="success" onClick={() => updateLeaveStatus(l.id, "approved")}><CheckCircleIcon fontSize="small" /></IconButton></Tooltip>
                              <Tooltip title="Reject"><IconButton size="small" color="error" onClick={() => updateLeaveStatus(l.id, "rejected")}><CancelIcon fontSize="small" /></IconButton></Tooltip>
                            </>
                          )}
                          {l.status !== "pending" && <Typography variant="caption" color="text.secondary">—</Typography>}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
            {leaveRequests.length > 0 && (
              <TablePagination component="div" count={leavePag.totalCount} page={leavePag.page} onPageChange={leavePag.onPageChange} rowsPerPage={leavePag.rowsPerPage} onRowsPerPageChange={leavePag.onRowsPerPageChange} rowsPerPageOptions={[10, 25, 50]} />
            )}
          </Box>
        )}

        {/* ══════ TAB 5 — Public Holidays ══════ */}
        {tab === 5 && (
          <Box sx={{ p: 3 }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>Public Holidays</Typography>
              <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
                <TextField type="number" size="small" label="Year" value={phYear} onChange={(e) => setPhYear(parseInt(e.target.value) || dayjs().year())} sx={{ width: 100 }} />
                <Button variant="contained" startIcon={<AddIcon />} onClick={() => setPhOpen(true)}>Add Holiday</Button>
              </Box>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Public holidays apply to all employees and are counted as paid days. Showing {filteredHolidays.length} holidays for {phYear}.
            </Typography>
            <TableContainer sx={{ borderRadius: 2, border: 1, borderColor: "divider" }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell>Day</TableCell>
                    <TableCell>Holiday Name</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredHolidays.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={99} align="center" sx={{ py: 6, color: "text.secondary" }}>
                        <CalendarMonthIcon sx={{ fontSize: 48, opacity: 0.3, mb: 1 }} /><br />
                        No public holidays for {phYear}. Click "Add Holiday" to add.
                      </TableCell>
                    </TableRow>
                  )}
                  {holPag.paginatedItems.map((h) => (
                    <TableRow key={h.id}>
                      <TableCell>{dayjs(h.date).format("DD/MMM/YYYY")}</TableCell>
                      <TableCell>{dayjs(h.date).format("dddd")}</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>{h.name}</TableCell>
                      <TableCell align="center">
                        <Tooltip title="Delete"><IconButton size="small" color="error" onClick={() => deletePublicHoliday(h.id)}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            {filteredHolidays.length > 0 && (
              <TablePagination component="div" count={holPag.totalCount} page={holPag.page} onPageChange={holPag.onPageChange} rowsPerPage={holPag.rowsPerPage} onRowsPerPageChange={holPag.onRowsPerPageChange} rowsPerPageOptions={[10, 25, 50]} />
            )}
          </Box>
        )}

      </Paper>

      {/* ═══ Employee Dialog ═══ */}
      <Dialog open={empOpen} onClose={() => setEmpOpen(false)} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 700, pb: 0 }}>
          {empForm.id ? "Edit Employee" : "Add New Employee"}
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Box sx={{ display: "flex", gap: 2, mb: 2, mt: 1 }}>
            <TextField fullWidth label="First Name" value={empForm.firstName} onChange={(e) => setEmpForm({ ...empForm, firstName: e.target.value })} />
            <TextField fullWidth label="Last Name" value={empForm.lastName} onChange={(e) => setEmpForm({ ...empForm, lastName: e.target.value })} />
          </Box>
          <TextField fullWidth label="Designation" placeholder="e.g. Junior Developer, Senior Manager" value={empForm.designation} onChange={(e) => setEmpForm({ ...empForm, designation: e.target.value })} sx={{ mb: 2 }} />
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Location</InputLabel>
            <Select value={empForm.branchId} label="Location" onChange={(e: SelectChangeEvent) => setEmpForm({ ...empForm, branchId: e.target.value })}>
              {locations.map((l) => <MenuItem key={l.id} value={l.id}>{l.name}</MenuItem>)}
            </Select>
          </FormControl>
          <TextField fullWidth label="Base Salary (monthly)" type="number" value={empForm.baseSalary || ""} onChange={(e) => setEmpForm({ ...empForm, baseSalary: parseFloat(e.target.value) || 0 })} sx={{ mb: 2 }} />
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Status</InputLabel>
            <Select value={empForm.status} label="Status" onChange={(e: SelectChangeEvent) => setEmpForm({ ...empForm, status: e.target.value })}>
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="inactive">Inactive</MenuItem>
              <MenuItem value="terminated">Terminated</MenuItem>
            </Select>
          </FormControl>
          {/* Personal Details */}
          <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, mb: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>Personal Details</Typography>
            <Box sx={{ display: "flex", gap: 2, mb: 2, flexWrap: "wrap" }}>
              <DatePicker label="Date of Joining" value={empForm.dateOfJoining ? dayjs(empForm.dateOfJoining) : null} onChange={(v: Dayjs | null) => setEmpForm({ ...empForm, dateOfJoining: v ? v.format("YYYY-MM-DD") : "" })} format="DD/MMM/YYYY" slotProps={{ textField: { sx: { flex: "1 1 180px" } } }} />
              <DatePicker label="Date of Birth" value={empForm.dob ? dayjs(empForm.dob) : null} onChange={(v: Dayjs | null) => setEmpForm({ ...empForm, dob: v ? v.format("YYYY-MM-DD") : "" })} format="DD/MMM/YYYY" slotProps={{ textField: { sx: { flex: "1 1 180px" } } }} />
              <FormControl sx={{ flex: "1 1 150px" }}>
                <InputLabel>Blood Group</InputLabel>
                <Select value={empForm.bloodGroup} label="Blood Group" onChange={(e: SelectChangeEvent) => setEmpForm({ ...empForm, bloodGroup: e.target.value })}>
                  <MenuItem value="">Not Specified</MenuItem>
                  {BLOOD_GROUPS.map((bg) => <MenuItem key={bg} value={bg}>{bg}</MenuItem>)}
                </Select>
              </FormControl>
            </Box>
            <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
              <TextField fullWidth label="Father's Name" value={empForm.fatherName} onChange={(e) => setEmpForm({ ...empForm, fatherName: e.target.value })} />
              <TextField fullWidth label="Mother's Name" value={empForm.motherName} onChange={(e) => setEmpForm({ ...empForm, motherName: e.target.value })} />
            </Box>
            <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
              <TextField fullWidth label="Educational Qualifications" placeholder="e.g. B.Tech, MBA" value={empForm.educationalQualifications} onChange={(e) => setEmpForm({ ...empForm, educationalQualifications: e.target.value })} />
              <TextField fullWidth label="Skills" placeholder="e.g. React, Excel, Accounting" value={empForm.skills} onChange={(e) => setEmpForm({ ...empForm, skills: e.target.value })} />
            </Box>
            <TextField fullWidth label="Notes" multiline rows={2} placeholder="Any additional notes about this employee..." value={empForm.notes} onChange={(e) => setEmpForm({ ...empForm, notes: e.target.value })} />
          </Paper>

        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setEmpOpen(false)} sx={{ mr: 1 }}>Cancel</Button>
          <Button variant="contained" onClick={saveEmployee} size="large">Save Employee</Button>
        </DialogActions>
      </Dialog>

      {/* ═══ Location Dialog ═══ */}
      <Dialog open={locOpen} onClose={() => setLocOpen(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 700 }}>{locForm.id ? "Edit Location" : "Add New Location"}</DialogTitle>
        <DialogContent>
          <TextField fullWidth label="Location Name" value={locForm.name} onChange={(e) => setLocForm({ ...locForm, name: e.target.value })} sx={{ mb: 2, mt: 1 }} />
          <TextField fullWidth label="Address" value={locForm.location} onChange={(e) => setLocForm({ ...locForm, location: e.target.value })} />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setLocOpen(false)} sx={{ mr: 1 }}>Cancel</Button>
          <Button variant="contained" onClick={saveLocation} size="large">Save Location</Button>
        </DialogActions>
      </Dialog>


      {/* ═══ Public Holiday Dialog ═══ */}
      <Dialog open={phOpen} onClose={() => setPhOpen(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 700 }}>Add Public Holiday</DialogTitle>
        <DialogContent>
          <DatePicker label="Date" value={phDate ? dayjs(phDate) : null} onChange={(v: Dayjs | null) => setPhDate(v ? v.format("YYYY-MM-DD") : "")} format="DD/MMM/YYYY" slotProps={{ textField: { fullWidth: true, sx: { mb: 2, mt: 1 } } }} />
          <TextField fullWidth label="Holiday Name" placeholder="e.g. Republic Day, Diwali" value={phName} onChange={(e) => setPhName(e.target.value)} />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setPhOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={savePublicHoliday}>Add Holiday</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={!!msg} autoHideDuration={3000} onClose={() => setMsg("")} anchorOrigin={{ vertical: "bottom", horizontal: "right" }}>
        <Alert severity={msgType} variant="filled" onClose={() => setMsg("")} sx={{ fontWeight: 600 }}>{msg}</Alert>
      </Snackbar>
    </Box>
  );
}
